import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { debounce } from "lodash";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Pagination,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Paper,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { StyledButton, inputStyle } from "components/StyledComponents/styledComponents";
import { CustomPaper } from "components/Styles/globalStyles";
import CurrencyInput from "react-currency-input-field";
import { useMaterialUIController } from "context";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents.js";
import { StyledCancelButton } from "components/StyledComponents/styledComponents";

export default function ConnectorPayment() {
  const jwt_token = Cookie.get("jwt_token");
  const [entries, setEntries] = useState([]);
  const [subDSAList, setSubDSAList] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentForm, setPaymentForm] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [errorMessage, setErrorMessage] = useState("");
  const MInput = inputStyle();
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split("T")[0]);
  const rowsPerPage = 5;
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState({
    actledId: 0,
    actledName: "",
  });
  const debouncedSearch = useMemo(() => debounce((value) => setSearch(value ?? ""), 300), []);
  const [isManualTransferAmount, setIsManualTransferAmount] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEntryId, setPendingEntryId] = useState(null);
  const [controller] = useMaterialUIController(); // Get context state
  const { user } = controller; // Destructure user

  useEffect(() => {
    fetchEntries();
    fetchBankAccountsData();
  }, []);
  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.getEntriesForConnector}=${user.companyId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token ?? ""}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch entries");
      const data = await response.json();
      const sanitizedData = Array.isArray(data)
        ? data.map((entry) => ({
            entryId: entry.entryId ?? null,
            disburseId: entry.disburseId ?? null,
            dsaId: entry.dsaId ?? null,
            subDsaId: entry.subDsaId ?? null,
            finananceID: entry.finananceID ?? null,
            applicationNumber: entry.applicationNumber ?? "-",
            customerName: entry.customerName ?? "-",
            entryDate: entry.entryDate ?? null,
            dsaName: entry.dsaName ?? "-",
            subDsaName: entry.subDsaName ?? "-",
            disbursedAmount: Number(entry.disbursedAmount) || 0,
            grossPayoutAmount: Number(entry.grossPayoutAmount) || 0,
            grossPayoutPercent: Number(entry.grossPayoutPercent) || 0,
            dsaPayoutPercent: Number(entry.dsaPayoutPercent) || 0,
            dsaPayoutAmount: Number(entry.dsaPayoutAmount) || 0,
            subDsaPayoutPercent: Number(entry.subDsaPayoutPercent) || 0,
            subDsaPaymentAmount: Number(entry.subDsaPaymentAmount) || 0,
            finalSubDSAGrossPayoutAmount: Number(entry.finalSubDSAGrossPayoutAmount) || 0,
            finainacialInstitutionName: entry.finainacialInstitutionName ?? "-",
          }))
        : [];

      setEntries(sanitizedData);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage("Failed to load disbursement entries.");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };
  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // Fetch Sub-DSA list
  useEffect(() => {
    const fetchSubDSAList = async () => {
      try {
        const response = await fetch(URLs.getConnectors, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt_token ?? ""}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch Sub-DSA list");
        const data = await response.json();
        const sanitizedData = Array.isArray(data)
          ? data.map((dsa) => ({
              DSA_ID: dsa.dsaId ?? null,
              DSAName: dsa.dsaName ?? "-",
            }))
          : [];
        setSubDSAList(sanitizedData);
      } catch (error) {
        setSubDSAList([]);
      }
    };
    fetchSubDSAList();
  }, []);

  const fetchBankAccountsData = async () => {
    setLoading(true);
    const url = `${URLs.headOfficeBanksAcc}/${user.companyId}`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt_token}`,
      },
    };
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setBankAccounts(data);
    } catch (error) {
      setAlert("Failed to fetch bank accounts", "error");
    }
    setLoading(false);
  };
  // Filter entries based on search
  const filteredEntries = useMemo(() => {
    if (!search) return entries;
    const query = (search ?? "").toLowerCase();
    return entries.filter((entry) =>
      [entry.dsaName, entry.subDSAName, entry.customerName, entry.applicationNumber].some((field) =>
        (field ?? "").toLowerCase().includes(query)
      )
    );
  }, [entries, search]);

  const handleBankChange = (event, newValue) => {
    setSelectedBank(newValue);
  };

  // Paginate entries
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Handle row toggle and initialize payment form
  const handleToggleRow = (entry) => {
    setSelectedRow(entry);
    const entryId = entry.entryId ?? null;
    if (expandedRow === entryId) {
      setExpandedRow(null);
      setPaymentForm({});
      setErrors({});
    } else {
      setExpandedRow(entryId);
      setPaymentForm({
        entryId: entryId,
        disburseId: entry.disburseId ?? null,
        subDSAId: entry.subDsaId ?? "",
        receiptAmount: Number(entry.grossPayoutAmount) || 0,
        newPayment: { amount: 0, percentage: 0 },
        dsaName: entry.dsaName ?? "-",
        transactionAmount: 0,
        cumulative: {
          subDSAPayout: Number(entry.subDsaPaymentAmount) || 0,
          subDSAPercentage: Number(entry.subDsaPayoutPercent) || 0,
        },
        tds: 0,
      });
      setErrors({});
    }
  };

  // Handle input changes
  const handleInputChange = (field, value, entry) => {
    const updatedForm = { ...paymentForm };
    const grossPayout = Number(entry.disbursedAmount) || 0;

    if (field === "percentage") {
      const percentage = parseFloat(value) || 0;
      const amount = grossPayout ? (grossPayout * percentage) / 100 : 0;
      const tds = grossPayout ? amount * 0.02 : 0;

      updatedForm.newPayment.percentage = Number(percentage.toFixed(3));
      updatedForm.newPayment.amount = Number(amount.toFixed(3));
      updatedForm.tds = Number(tds.toFixed(3));
    } else if (field === "amount") {
      const amount = parseFloat(value) || 0;
      const percentage = grossPayout ? (amount / grossPayout) * 100 : 0;
      const tds = amount * 0.02;

      updatedForm.newPayment.amount = Number(amount.toFixed(3));
      updatedForm.newPayment.percentage = Number(percentage.toFixed(3));
      updatedForm.tds = Number(tds.toFixed(3));
    } else if (field === "tds") {
      updatedForm.tds = Number(parseFloat(value || 0).toFixed(3));
    } else if (field === "subDSAId") {
      updatedForm.subDSAId = value ?? "";
    } else if (field === "transactionAmount") {
      const netAmount = parseFloat(value) || 0;
      const grossPayout = Number(entry.disbursedAmount) || 0;
      const amount = netAmount / 0.98; // Reverse of 2% TDS deduction
      const tds = amount - netAmount;
      const percentage = grossPayout ? (amount / grossPayout) * 100 : 0;

      updatedForm.transactionAmount = Number(netAmount.toFixed(3));
      updatedForm.newPayment.amount = Number(amount.toFixed(3));
      updatedForm.newPayment.percentage = Number(percentage.toFixed(3));
      updatedForm.tds = Number(tds.toFixed(3));
    }

    updatedForm.cumulative = {
      subDSAPayout:
        Number(selectedRow?.subDsaPaymentAmount ?? 0) + Number(updatedForm.newPayment.amount ?? 0),
      subDSAPercentage:
        Number(selectedRow?.subDsaPayoutPercent ?? 0) +
        Number(updatedForm.newPayment.percentage ?? 0),
    };
    updatedForm.transactionAmount = Number(
      (updatedForm.newPayment.amount - updatedForm.tds).toFixed(3)
    );
    setPaymentForm(updatedForm);
    validateForm(updatedForm);
  };
  const handleDateChange = (event) => {
    setTransactionDate(event.target.value); // Will be in YYYY-MM-DD format
  };

  const validateForm = (form) => {
    const newErrors = {};

    // Bank validation
    if (bankAccounts.length > 0 && (!selectedBank || !selectedBank.actledId)) {
      newErrors.bank = "Bank account is required";
    }

    // Sub-DSA validation
    if (!form.subDSAId) {
      newErrors.subDSAId = "Sub-DSA is required";
    }

    // Amount validation
    if (!form.newPayment?.amount || Number(form.newPayment.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
    }

    // Percentage validation
    if (!form.newPayment?.percentage || Number(form.newPayment.percentage) <= 0) {
      newErrors.percentage = "Valid percentage is required";
    }

    // TDS validation
    if (form.tds === undefined || isNaN(form.tds) || Number(form.tds) < 0) {
      newErrors.tds = "Valid TDS is required";
    }

    // Set and return
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit payment
  const handleSubmit = async (entryId) => {
    if (!validateForm(paymentForm)) return;

    setLoading(true);
    if (!transactionDate) {
      setAlert("Please Select Amount Transaction Date", "error");
    }

    const payload = {
      entryId: entryId,
      tranDate: transactionDate,
      dsa_id: user.companyId,
      subDsaId: paymentForm.subDSAId ?? null,
      disburseId: paymentForm.disburseId ?? null,
      subDsaPayoutPercent: Number(paymentForm.cumulative?.subDSAPercentage ?? 0),
      subDsaPaymentAmount: Number(paymentForm.cumulative?.subDSAPayout ?? 0),
      finalSubDsaGrossPayoutAmount: Number(paymentForm.transactionAmount ?? 0),
      tds: Number(paymentForm.tds ?? 0),
      headOfficeBankID: selectedBank.actledId ?? 0,
      subDsaName: subDSAList.find((dsa) => dsa.DSA_ID === paymentForm.subDSAId)?.DSAName ?? "-",
    };

    try {
      const response = await fetch(URLs.newConnectorPayment, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${jwt_token ?? ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const textResponse = await response.text();

      if (!response.ok) throw new Error(textResponse || "Failed to submit payment");

      // Show success toast or alert with plain response
      setAlert(`Payment Success `);
      // Reset form state
      setExpandedRow(null);
      setPaymentForm({});
      setErrors({});
      fetchEntries(); // Refresh entries after successful payment
    } catch (error) {
      setAlert(`Payment failed:`, "error");
      setErrorMessage("Failed to submit payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: "bold", color: "#2F2F2F" }}>
          Confirm Payment Submission
        </DialogTitle>
        <DialogContent sx={{ fontSize: 14, color: "#2F2F2F" }}>
          Are you sure you want to submit this payment?
        </DialogContent>
        <DialogActions>
          <StyledCancelButton
            onClick={() => setConfirmOpen(false)}
            sx={{ fontSize: 12, color: "#333" }}
          >
            Cancel
          </StyledCancelButton>
          <StyledButton
            onClick={() => {
              setConfirmOpen(false);
              handleSubmit(pendingEntryId);
            }}
          >
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          mt: 1,
          borderRadius: "8px",
          p: 2,
          flexDirection: "column",
          backgroundColor: "#f8fafc", // Light gray background
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0, // Adjust as needed
            zIndex: 10,
            backgroundColor: "#f9fafb", // Optional: to avoid overlap artifacts
            p: 2,
            pl: 0,

            maxWidth: "300px",
            mb: 0,
            borderRadius: "8px",
          }}
        >
          <TextField
            fullWidth
            label="Search by DSA, SubDSA, Customer, or App No."
            variant="outlined"
            value={search ?? ""}
            onChange={(e) => {
              debouncedSearch(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": { borderColor: "#d1d5db" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
              "& .MuiInputLabel-root": {
                color: "#6b7280",
                "&.Mui-focused": { color: "#3b82f6" },
              },
            }}
            inputProps={{ "aria-label": "Search disbursements" }}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            borderRadius: "8px",
            backgroundColor: "#fff",
            width: "100%",
            fontSize: "0.875rem", // 14px
            minWidth: "600px",
            maxHeight: "800px",
            "&::-webkit-scrollbar": {
              display: "none",
            },

            // ✅ Hide scrollbar (for Firefox)
            scrollbarWidth: "none", // hides scrollbar
            msOverflowStyle: "none", // hides scrollbar in IE 10
          }}
          role="grid"
          aria-label="Disbursement entries"
        >
          {/* Header Row */}
          <Grid
            container
            sx={{
              backgroundColor: "#333333",
              color: "#ffffff",
              position: "sticky",
              height: "60px",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e5e7eb",
              py: 1.5,
            }}
            role="row"
          >
            <Grid
              item
              xs={12}
              sm={1.5}
              md={1.5}
              sx={{ fontWeight: 700, px: 2 }}
              role="columnheader"
            >
              Date
            </Grid>
            <Grid
              item
              xs={12}
              sm={1.5}
              md={1.5}
              sx={{ fontWeight: 700, px: 2 }}
              role="columnheader"
            >
              App No
            </Grid>
            <Grid item xs={12} sm={2} md={2} sx={{ fontWeight: 700, px: 2 }} role="columnheader">
              Customer
            </Grid>
            <Grid item xs={12} sm={2} md={2} sx={{ fontWeight: 700, px: 2 }} role="columnheader">
              DSA
            </Grid>
            <Grid item xs={12} sm={2} md={2} sx={{ fontWeight: 700, px: 2 }} role="columnheader">
              Provider Name
            </Grid>
            <Grid
              item
              xs={12}
              sm={1.5}
              md={1.5}
              sx={{ fontWeight: 700, px: 2 }}
              role="columnheader"
            >
              Disbursement
            </Grid>
            <Grid
              item
              xs={12}
              sm={1.5}
              md={1.5}
              sx={{ fontWeight: 700, px: 2, textAlign: "center" }}
              role="columnheader"
            >
              Actions
            </Grid>
          </Grid>

          {/* Data Rows */}
          {loading ? (
            <Box sx={{ textAlign: "center", py: 2 }} role="row">
              <Typography variant="body2" color="#6b7280">
                Loading...
              </Typography>
            </Box>
          ) : paginatedEntries.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2 }} role="row">
              <Typography variant="body2" color="#6b7280">
                {errorMessage || "No Data"}
              </Typography>
            </Box>
          ) : (
            <AnimatePresence>
              {paginatedEntries.map((entry) => (
                <React.Fragment key={entry.entryId ?? `entry-${Math.random()}`}>
                  <motion.div
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    role="row"
                    style={{ overflow: "hidden" }}
                  >
                    <Grid
                      container
                      sx={{
                        borderBottom: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "#070707ff",
                        fontWeight: 450,
                        py: 1.5,
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={1.5}
                        md={1.5}
                        sx={{ color: "#374151", px: 2 }}
                        role="cell"
                      >
                        {entry.entryDate
                          ? (() => {
                              const date = new Date(entry.entryDate);
                              const day = date.toLocaleString("en-IN", { day: "2-digit" });
                              const month = date.toLocaleString("en-IN", { month: "short" });
                              const year = date.getFullYear();
                              return `${day}-${month}-${year}`;
                            })()
                          : "-"}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={1.5}
                        md={1.5}
                        sx={{ color: "#374151", px: 2 }}
                        role="cell"
                      >
                        {entry.applicationNumber ?? "-"}
                      </Grid>
                      <Grid item xs={12} sm={2} md={2} sx={{ color: "#374151", px: 2 }} role="cell">
                        {entry.customerName ?? "-"}
                      </Grid>
                      <Grid item xs={12} sm={2} md={2} sx={{ color: "#374151", px: 2 }} role="cell">
                        {entry.dsaName ?? "-"}
                      </Grid>
                      <Grid item xs={12} sm={2} md={2} sx={{ color: "#374151", px: 2 }} role="cell">
                        {entry.finainacialInstitutionName ?? "-"}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={1.5}
                        md={1.5}
                        sx={{ color: "#374151", px: 2 }}
                        role="cell"
                      >
                        ₹{(entry.disbursedAmount ?? 0).toLocaleString("en-IN")}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={1.5}
                        md={1.5}
                        sx={{ px: 2, textAlign: "center" }}
                        role="cell"
                      >
                        <StyledButton
                          variant="contained"
                          size="small"
                          onClick={() => handleToggleRow(entry)}
                          sx={{
                            // Base styles for the button
                            backgroundColor:
                              expandedRow === (entry.entryId ?? null) ? "#ffffff" : "#E59400",
                            color: expandedRow === (entry.entryId ?? null) ? "#000000" : "#ffffff",
                            border:
                              expandedRow === (entry.entryId ?? null)
                                ? "1px solid #000000"
                                : "none",
                            textTransform: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            height: "35px",
                            px: 2,
                            // Ensure no default MUI styles interfere
                            boxShadow: "none",
                            "&:hover": {
                              backgroundColor: "#ffffff",
                              color: "#333333",
                              border: "1px solid #000000",
                              boxShadow: "none",
                            },
                            // Active state
                            "&:active": {
                              backgroundColor: "#E59400",
                              color: "#ffffff",
                              border: "none",
                            },
                          }}
                          aria-expanded={expandedRow === (entry.entryId ?? null)}
                          aria-controls={`payment-panel-${entry.entryId ?? "unknown"}`}
                        >
                          {expandedRow === (entry.entryId ?? null) ? "Close" : "Make Payment"}
                        </StyledButton>
                      </Grid>
                    </Grid>
                  </motion.div>
                  {expandedRow === (entry.entryId ?? null) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      role="row"
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Paper
                                elevation={3}
                                sx={{
                                  p: 3,
                                  mt: 1,
                                  borderRadius: 2,
                                  backgroundColor: "#f9fafb",
                                  height: "100%",
                                }}
                              >
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                  Payout Summary
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                  <Typography
                                    variant="body3"
                                    color="text.secondary"
                                    sx={{ mt: 2, fontWeight: "400" }}
                                  >
                                    Disbursement Amount
                                  </Typography>
                                  <Typography variant="h6" fontWeight="bold">
                                    ₹{(selectedRow?.disbursedAmount ?? 0).toLocaleString("en-IN")}
                                  </Typography>
                                </Box>
                                {[
                                  {
                                    label: "Gross Payout",
                                    value: selectedRow?.grossPayoutAmount,
                                    percent: selectedRow?.grossPayoutPercent,
                                  },
                                  {
                                    label: "DMA Payout",
                                    value: selectedRow?.dsaPayoutAmount,
                                    percent: selectedRow?.dsaPayoutPercent,
                                  },
                                ].map((item, index) => (
                                  <Box key={index} sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ mt: 2, fontWeight: "400" }}
                                    >
                                      {item.label}
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                      ₹{(item.value ?? 0).toLocaleString("en-IN")} (
                                      {(item.percent ?? 0).toFixed(2)}%)
                                    </Typography>
                                  </Box>
                                ))}
                                <Box sx={{ position: "relative", mb: 1 }}>
                                  {/* Label for Sub-DSA */}
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: "-12px",
                                      left: "12px",
                                      px: 1.5,
                                      py: "2px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      color: "white",
                                      border: "1px solid black",
                                      borderRadius: "4px",
                                      zIndex: 1,
                                      backgroundColor:
                                        selectedRow?.subDsaId === -1 ? "#6b7280" : "orange", // gray for no subdsa
                                    }}
                                  >
                                    {selectedRow?.subDsaId === -1
                                      ? "No Connector"
                                      : selectedRow?.subDsaName}
                                  </Box>

                                  {/* Payout Section */}
                                  <Box
                                    sx={{
                                      p: 2,
                                      mt: 3,
                                      borderRadius: "8px",
                                      backgroundColor: "#fff",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                      border: "1px solid #ddd",
                                    }}
                                  >
                                    <Typography
                                      variant="body3"
                                      color="text.secondary"
                                      sx={{ fontWeight: 700, mb: 0.5 }}
                                    >
                                      Previously Paid
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                      ₹
                                      {(selectedRow?.subDsaPaymentAmount ?? 0).toLocaleString(
                                        "en-IN"
                                      )}{" "}
                                      ({(selectedRow?.subDsaPayoutPercent ?? 0).toFixed(2)}%)
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>

                            {/* Right Card: Cumulative Info */}
                            <Grid item xs={12} sm={6}>
                              <Paper
                                elevation={3}
                                sx={{
                                  p: 3,
                                  mt: 1,
                                  borderRadius: 2,
                                  backgroundColor: "#f9fafb",
                                  height: "100%",
                                }}
                              >
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                  Total Payout Summary
                                </Typography>
                                <Typography variant="body3" color="text.secondary">
                                  Total Amount Paid
                                </Typography>
                                <Typography sx={{ mb: 1 }} variant="h6" fontWeight="bold">
                                  ₹
                                  {(paymentForm.cumulative?.subDSAPayout ?? 0).toLocaleString(
                                    "en-IN"
                                  )}
                                </Typography>
                                <Typography
                                  variant="body3"
                                  color="text.secondary"
                                  sx={{ mt: 2, fontWeight: "400" }}
                                >
                                  Total Paid Percentage
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {(paymentForm.cumulative?.subDSAPercentage ?? 0).toFixed(2)}%
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Right Side – Input Form */}
                        <Grid item xs={12} md={7}>
                          <Box
                            sx={{
                              p: 2,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={isManualTransferAmount}
                                  onChange={(e) => setIsManualTransferAmount(e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Enter Net Transfer Amount manually"
                            />
                            {isManualTransferAmount ? (
                              <TextField
                                fullWidth
                                label="Net Transfer Amount (after TDS)"
                                required
                                value={paymentForm.transactionAmount ?? ""}
                                InputProps={{
                                  inputComponent: CurrencyInput,
                                  inputProps: {
                                    value: paymentForm.transactionAmount ?? "",
                                    prefix: "₹ ",
                                    decimalsLimit: 3,
                                    intlConfig: { locale: "en-IN" },
                                    onValueChange: (value) =>
                                      handleInputChange("transactionAmount", value, selectedRow),
                                  },
                                }}
                                sx={{ ...MInput.b, mb: 3, width: "50%", minWidth: 200 }}
                                InputLabelProps={{
                                  sx: {
                                    color: MInput.a,
                                  },
                                }}
                              />
                            ) : (
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                                Net Transfer Amount (after TDS deduction): ₹
                                {(paymentForm.transactionAmount ?? 0).toLocaleString("en-IN")}
                              </Typography>
                            )}

                            <Grid container spacing={2}>
                              {/* Bank Select */}
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  label="Payment Date"
                                  type="date"
                                  value={transactionDate} // Controlled input
                                  onChange={handleDateChange} // Updates state
                                  fullWidth
                                  sx={{ ...MInput.b }}
                                  InputLabelProps={{
                                    shrink: true,
                                    sx: { color: MInput.a },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                {/* Autocomplete for Bank */}
                                <Autocomplete
                                  options={bankAccounts}
                                  getOptionLabel={(option) => `${option.actledName}`}
                                  value={selectedBank}
                                  onChange={handleBankChange}
                                  PaperComponent={CustomPaper}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Bank"
                                      sx={{
                                        height: 43,
                                        ...MInput.b,
                                        "& .MuiInputBase-root": {
                                          height: "100%",
                                          minHeight: 43,
                                        },
                                      }}
                                      InputLabelProps={{
                                        sx: {
                                          color: MInput.a, // Ensure this applies to the label
                                          "&.Mui-focused": {
                                            color: "red", // Focused label color
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                />
                                {errors.bank && (
                                  <Typography color="#dc2626" variant="caption" sx={{ mt: 1 }}>
                                    {errors.bank}
                                  </Typography>
                                )}
                              </Grid>

                              {/* SubDSA Select */}
                              <Grid item xs={12} sm={6} md={4}>
                                {/* Autocomplete for SubDSA */}
                                <Autocomplete
                                  fullWidth
                                  options={subDSAList}
                                  getOptionLabel={(option) => option.DSAName ?? "-"}
                                  value={
                                    subDSAList.find((dsa) => dsa.DSA_ID === paymentForm.subDSAId) ||
                                    null
                                  }
                                  onChange={(event, newValue) => {
                                    const newId = newValue ? newValue.DSA_ID : "";
                                    handleInputChange("subDSAId", newId, entry);
                                  }}
                                  PaperComponent={CustomPaper}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select SubDSA"
                                      placeholder="Search SubDSA"
                                      error={!!errors.subDSAId}
                                      helperText={errors.subDSAId ?? ""}
                                      sx={{
                                        height: 43,
                                        ...MInput.b,
                                        "& .MuiInputBase-root": {
                                          height: "100%",
                                          minHeight: 43,
                                        },
                                      }}
                                      InputLabelProps={{
                                        sx: {
                                          color: MInput.a, // Ensure this applies to the label
                                          "&.Mui-focused": {
                                            color: "red", // Focused label color
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  isOptionEqualToValue={(option, value) =>
                                    option.DSA_ID === value?.DSA_ID
                                  }
                                  clearOnEscape
                                />
                              </Grid>

                              {/* Percentage */}
                              <Grid item xs={6} sm={3} md={4}>
                                {/* TextField for Percentage */}
                                <TextField
                                  fullWidth
                                  label="Percentage"
                                  type="number"
                                  value={paymentForm.newPayment?.percentage ?? ""}
                                  onChange={(e) =>
                                    handleInputChange("percentage", e.target.value, entry)
                                  }
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  }}
                                  sx={MInput.b}
                                  InputLabelProps={{
                                    sx: {
                                      color: MInput.a,
                                    },
                                  }}
                                />
                                {errors.percentage && (
                                  <Typography color="#dc2626" variant="caption" sx={{ mt: 1 }}>
                                    {errors.percentage}
                                  </Typography>
                                )}
                              </Grid>

                              {/* Amount */}
                              <Grid item xs={6} sm={3} md={4}>
                                {/* TextField for Amount */}
                                <TextField
                                  fullWidth
                                  label="Amount"
                                  required
                                  value={paymentForm.newPayment?.amount ?? ""}
                                  InputProps={{
                                    inputComponent: CurrencyInput,
                                    inputProps: {
                                      value: paymentForm.newPayment?.amount ?? "",
                                      prefix: "₹ ",
                                      decimalsLimit: 2,
                                      intlConfig: { locale: "en-IN" },
                                      onValueChange: (value) =>
                                        handleInputChange("amount", value, entry),
                                    },
                                  }}
                                  sx={MInput.b}
                                  InputLabelProps={{
                                    sx: {
                                      color: MInput.a,
                                    },
                                  }}
                                />
                                {errors.amount && (
                                  <Typography color="#dc2626" variant="caption" sx={{ mt: 1 }}>
                                    {errors.amount}
                                  </Typography>
                                )}
                              </Grid>

                              {/* TDS */}
                              <Grid item xs={6} sm={3} md={4}>
                                {/* TextField for TDS */}
                                <TextField
                                  fullWidth
                                  label="TDS"
                                  required
                                  value={paymentForm.tds ?? ""}
                                  InputProps={{
                                    inputComponent: CurrencyInput,
                                    inputProps: {
                                      value: paymentForm.tds ?? "",
                                      prefix: "₹ ",
                                      decimalsLimit: 2,
                                      intlConfig: { locale: "en-IN" },
                                      onValueChange: (value) =>
                                        setPaymentForm((prev) => ({
                                          ...prev,
                                          tds: value,
                                        })),
                                    },
                                  }}
                                  sx={MInput.b}
                                  InputLabelProps={{
                                    sx: {
                                      color: MInput.a,
                                    },
                                  }}
                                />
                                {errors.tds && (
                                  <Typography color="#dc2626" variant="caption" sx={{ mt: 1 }}>
                                    {errors.tds}
                                  </Typography>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <StyledButton
                                  variant="contained"
                                  color="success"
                                  fullWidth
                                  onClick={() => {
                                    if (!validateForm(paymentForm)) return;
                                    setPendingEntryId(entry.entryId ?? null);
                                    setConfirmOpen(true);
                                  }}
                                  disabled={Object.keys(errors).length > 0 || loading}
                                  sx={{
                                    textTransform: "none",
                                    borderRadius: "6px",
                                    textTransform: "none",
                                    borderRadius: "6px",
                                    backgroundColor: "#E59400",
                                    cursor: "pointer",
                                    height: "35px",
                                    "&:hover": {
                                      backgroundColor: "#ffffff",
                                      color: "#333333",
                                      border: "1px solid #000000",
                                      boxShadow: "none",
                                    },
                                    "&:focus": { outline: "2px solid #22c55e" },
                                  }}
                                >
                                  {loading ? "Submitting..." : "Submit"}
                                </StyledButton>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      </Grid>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </AnimatePresence>
          )}
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Pagination
            count={Math.ceil(filteredEntries.length / rowsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  backgroundColor: "#1d1d1eff",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#b6be15ff" },
                },
                "&:hover": { backgroundColor: "#e5e7eb" },
              },
            }}
          />
        </Box>
        {errorMessage && !loading && (
          <Typography color="#dc2626" variant="body2" align="center" mt={2}>
            {errorMessage}
          </Typography>
        )}
      </Box>
    </DashboardLayout>
  );
}
